import type { AdminBillingRetry } from "@/types/admin-billing-retry";
import type { MockUserId } from "@/lib/api/mock-data/users.mock";

/**
 * Mock billing-retry projections — mirror the gateway `AdminBillingRetry`
 * (L69 safe projection of `billing_retry_states`) returned by
 * `GET /admin/users/{user_id}/billing-retry`.
 *
 * Only the dunning-path fixtures carry a real retry row; healthy users get the
 * `source: "none"` shape (the endpoint still returns 200, the UI shows a "no
 * active billing retry" fallback). No raw Stripe message, no decline_code, no
 * customer/invoice id, no secret — `failureCode` is a safe machine token only.
 */
const MAX_RETRIES = 5;

const NONE: AdminBillingRetry = {
  kind: null,
  status: null,
  retryCount: null,
  maxRetries: MAX_RETRIES,
  failedAt: null,
  lastAttemptAt: null,
  nextRetryAt: null,
  terminalAt: null,
  failureCode: null,
  createdAt: null,
  updatedAt: null,
  source: "none",
};

export const MOCK_BILLING_RETRY: Record<MockUserId, AdminBillingRetry> = {
  u_001_alice_active: NONE,
  u_002_bob_trial_pending: NONE,
  u_003_cara_trial_ending: NONE,
  // Trial day-31 charge failed — actively retrying.
  u_004_dave_card_failed: {
    kind: "trial_conversion",
    status: "retrying",
    retryCount: 1,
    maxRetries: MAX_RETRIES,
    failedAt: "2026-05-22T11:15:00Z",
    lastAttemptAt: "2026-05-22T11:15:00Z",
    nextRetryAt: "2026-05-25T11:15:00Z",
    terminalAt: null,
    failureCode: "card_declined",
    createdAt: "2026-05-22T11:15:00Z",
    updatedAt: "2026-05-22T11:15:00Z",
    source: "real",
  },
  // Trial charged off — retries exhausted (terminal).
  u_005_erin_charged_off: {
    kind: "trial_conversion",
    status: "exhausted",
    retryCount: 5,
    maxRetries: MAX_RETRIES,
    failedAt: "2026-03-17T09:30:00Z",
    lastAttemptAt: "2026-03-29T09:30:00Z",
    nextRetryAt: null,
    terminalAt: "2026-03-29T09:30:00Z",
    failureCode: "card_declined",
    createdAt: "2026-03-17T09:30:00Z",
    updatedAt: "2026-03-29T09:30:00Z",
    source: "real",
  },
  // Renewal charge failed — actively retrying.
  u_006_finn_past_due: {
    kind: "renewal",
    status: "retrying",
    retryCount: 2,
    maxRetries: MAX_RETRIES,
    failedAt: "2026-06-05T16:05:00Z",
    lastAttemptAt: "2026-06-09T16:05:00Z",
    nextRetryAt: "2026-06-13T16:05:00Z",
    terminalAt: null,
    failureCode: "insufficient_funds",
    createdAt: "2026-06-05T16:05:00Z",
    updatedAt: "2026-06-09T16:05:00Z",
    source: "real",
  },
  u_007_gina_suspended: NONE,
  u_008_hugo_cancelled: NONE,
};
